import { type SnapUploadResult, snapUploadResultSchema } from '../api/schema.js'

/** 100장을 고른 폰에서 썸네일 100개를 동시 디코딩하면 브라우저가 멈춘다. */
export const PREVIEW_LIMIT = 12

const IMAGE_EXT = /\.(jpe?g|png|heic|heif|webp|gif|avif)$/i
const VIDEO_EXT = /\.(mp4|mov|m4v|avi|mkv|webm|3gp)$/i

export function isUploadable(file: File): boolean {
  const type = file.type.toLowerCase()
  if (type.startsWith('image/') || type.startsWith('video/')) return true
  return IMAGE_EXT.test(file.name) || VIDEO_EXT.test(file.name)
}

/**
 * 진행이 이만큼 멈추면 포기한다.
 *
 * 총 시간 제한이 아니다 — 300MB 영상은 혼잡한 모바일 데이터에서 정상적으로도
 * 10분 넘게 걸린다. 총량으로 자르면 성공했을 업로드를 죽인다. 여기서 자르려는 것은
 * "진행이 완전히 멈춘 연결"뿐이고, 바이트가 조금이라도 올라가면 시계는 되감긴다.
 */
export const UPLOAD_STALL_TIMEOUT_MS = 60_000

export interface UploadOptions {
  /** 하객이 취소를 누르면 이 signal로 요청을 끊는다. */
  signal?: AbortSignal
  stallTimeoutMs?: number
}

/**
 * fetch는 업로드 진행률을 주지 않는다. 동영상은 수백 MB가 예사라
 * 진행률 없이 스피너만 돌면 하객이 앱을 닫는다.
 */
export function uploadSnaps(
  files: File[],
  onProgress: (fraction: number) => void,
  options: UploadOptions = {},
): Promise<SnapUploadResult> {
  const { signal, stallTimeoutMs = UPLOAD_STALL_TIMEOUT_MS } = options

  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new Error('aborted'))
      return
    }

    const form = new FormData()
    for (const file of files) form.append('files', file, file.name)

    const request = new XMLHttpRequest()
    let stallTimer: ReturnType<typeof setTimeout> | undefined

    const stopWatching = () => {
      clearTimeout(stallTimer)
      signal?.removeEventListener('abort', handleAbortSignal)
    }
    const armStallTimer = () => {
      clearTimeout(stallTimer)
      stallTimer = setTimeout(() => {
        // 먼저 거절해 두면 abort()가 부르는 'aborted'보다 이 사유가 이긴다.
        stopWatching()
        reject(new Error('timeout'))
        request.abort()
      }, stallTimeoutMs)
    }
    function handleAbortSignal() {
      request.abort()
    }

    signal?.addEventListener('abort', handleAbortSignal, { once: true })

    request.open('POST', '/api/snaps')
    request.upload.addEventListener('progress', (event) => {
      armStallTimer()
      if (event.lengthComputable) onProgress(event.loaded / event.total)
    })
    request.addEventListener('load', () => {
      stopWatching()
      if (request.status === 423) {
        reject(new Error('not_open_yet'))
        return
      }
      if (request.status === 507) {
        reject(new Error('insufficient_storage'))
        return
      }
      if (request.status === 429) {
        reject(new Error('too_many_requests'))
        return
      }
      if (request.status < 200 || request.status >= 300) {
        reject(new Error(`http ${request.status}`))
        return
      }
      try {
        resolve(snapUploadResultSchema.parse(JSON.parse(request.responseText)))
      } catch (error) {
        reject(error as Error)
      }
    })
    request.addEventListener('error', () => {
      stopWatching()
      reject(new Error('network'))
    })
    request.addEventListener('abort', () => {
      stopWatching()
      reject(new Error('aborted'))
    })
    request.send(form)
    armStallTimer()
  })
}

export function summarize(result: SnapUploadResult): string {
  const ok = result.accepted.length
  const bad = result.rejected.length
  if (ok === 0) return `${bad}장은 보내지 못했어요`
  if (bad === 0) return `사진 ${ok}장, 감사히 받았어요`
  return `사진 ${ok}장 보냈어요 · ${bad}장은 보내지 못했어요`
}
